/* District Bangle — global.js
   The shared runtime: a base custom element, an event bus, and accessibility
   utilities. Vanilla ES modules, no framework (ADR 0001/0007). Exposed as the
   bare specifier @theme/global via the import map in layout/theme.liquid.
   See engineering-os/docs/04-architecture/javascript-architecture.md */

/* ---- Event bus ---- */
export const bus = {
  on(name, fn) { document.addEventListener(name, fn); },
  off(name, fn) { document.removeEventListener(name, fn); },
  emit(name, detail) { document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true })); },
};

/* ---- Accessibility utilities ---- */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';

export function getFocusable(root) {
  return Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
}

export function trapFocus(container, opener) {
  const focusables = getFocusable(container);
  (focusables[0] || container).focus();
  function onKey(e) {
    if (e.key !== 'Tab' || !focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
  container.addEventListener('keydown', onKey);
  return function release() {
    container.removeEventListener('keydown', onKey);
    if (opener && typeof opener.focus === 'function') opener.focus();
  };
}

let scrollLocks = 0;
export function lockScroll() { if (scrollLocks++ === 0) document.body.style.overflow = 'hidden'; }
export function unlockScroll() { if (scrollLocks > 0 && --scrollLocks === 0) document.body.style.overflow = ''; }

/* Currency-aware money formatter (uses window.theme.moneyFormat). */
export function money(cents) {
  const amount = (cents / 100).toFixed((window.theme && window.theme.currencyDecimals != null) ? window.theme.currencyDecimals : 2);
  const fmt = (window.theme && window.theme.moneyFormat) || '${{amount}}';
  return fmt.replace(/\{\{\s*amount[^}]*\}\}/, amount);
}

/* What happens after a successful add is the merchant's "After adding to cart"
   choice, read against the Cart type: with the cart set to Page there is no
   drawer in the document, so "Open the cart" can only mean the cart page. Every
   path that adds to the cart calls this rather than deciding for itself —
   otherwise the setting would govern the product form and nothing else. */
export function afterAdd() {
  const cfg = (window.theme && window.theme.settings) || {};
  const choice = cfg.cartAfterAdd || 'open';
  const hasDrawer = cfg.cartType !== 'page';
  if (choice === 'cart' || (choice === 'open' && !hasDrawer)) {
    window.location.href = window.theme.routes.cart;
    return;
  }
  bus.emit('cart:updated', { open: choice === 'open' });
}

/* ---- Base element ----
   Idempotent connect, teardown on disconnect, re-init on theme-editor section
   load. Every interactive component extends this. */
export class BaseElement extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;
    this._teardowns = [];
    try { this.init(); } catch (e) { console.error(this.tagName, e); }
  }
  disconnectedCallback() {
    this._bound = false;
    (this._teardowns || []).forEach((fn) => { try { fn(); } catch (_) {} });
    this._teardowns = [];
    if (typeof this.teardown === 'function') this.teardown();
  }
  onCleanup(fn) { this._teardowns.push(fn); }
  listen(target, type, handler, opts) {
    target.addEventListener(type, handler, opts);
    this.onCleanup(() => target.removeEventListener(type, handler, opts));
  }
  init() {}
}

export function define(name, ctor) {
  if (!customElements.get(name)) customElements.define(name, ctor);
}

/* Re-initialise a custom element after the theme editor injects/replaces it. */
export function reviveOnSectionLoad(name) {
  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelectorAll(name).forEach((el) => { el._bound = false; el.connectedCallback(); });
  });
}

/* ============================================================
   Global chrome components
   ============================================================ */

/* ---- Announcement bar: rotate (motion-aware, pausable) + dismiss ---- */
class AnnouncementBar extends BaseElement {
  init() {
    this.items = Array.from(this.querySelectorAll('[data-announcement]'));
    const key = 'db:announcement-dismissed';
    const close = this.querySelector('[data-close]');
    if (close) this.listen(close, 'click', () => { this.hidden = true; try { localStorage.setItem(key, this.dataset.hash || '1'); } catch (_) {} });
    if (this.hasAttribute('data-dismissible')) { try { if (localStorage.getItem(key) === this.dataset.hash) this.hidden = true; } catch (_) {} }

    const motionOk = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.dataset.rotate === 'true' && this.items.length > 1 && motionOk) {
      this.rotate();
      this.pauseBtn = this.querySelector('[data-pause]');
      if (this.pauseBtn) { this.pauseBtn.hidden = false; this.listen(this.pauseBtn, 'click', () => this.toggle()); }
      this.listen(this, 'mouseenter', () => this.stop());
      this.listen(this, 'focusin', () => this.stop());
      this.listen(this, 'mouseleave', () => { if (!this._paused) this.rotate(); });
      this.listen(this, 'focusout', (e) => { if (!this._paused && !this.contains(e.relatedTarget)) this.rotate(); });
    }
  }
  stop() { if (this._timer) { clearInterval(this._timer); this._timer = null; } }
  toggle() {
    this._paused = !this._paused;
    if (this._paused) this.stop(); else this.rotate();
    if (this.pauseBtn) this.pauseBtn.setAttribute('aria-pressed', this._paused ? 'true' : 'false');
  }
  rotate() {
    this.stop();
    let i = this.items.findIndex((el) => !el.hidden);
    if (i < 0) i = 0;
    this.items.forEach((el, idx) => { el.hidden = idx !== i; });
    const every = Number(this.dataset.interval || 5000);
    this._timer = setInterval(() => { this.items[i].hidden = true; i = (i + 1) % this.items.length; this.items[i].hidden = false; }, every);
    this.onCleanup(() => this.stop());
  }
}

/* ---- Quantity stepper ---- */
class QuantityInput extends BaseElement {
  init() {
    this.input = this.querySelector('input');
    const dec = this.querySelector('[data-dec]');
    const inc = this.querySelector('[data-inc]');
    if (dec) this.listen(dec, 'click', () => this.step(-1));
    if (inc) this.listen(inc, 'click', () => this.step(1));
  }
  step(dir) {
    const min = Number(this.input.min || 1);
    this.input.value = Math.max(min, (Number(this.input.value) || min) + dir);
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

/* ---- Generic overlay drawer / modal (menu, cart, quick view, fit finder) ---- */
export class OverlayDrawer extends BaseElement {
  init() {
    this.panel = this.querySelector('[data-panel]') || this;
    document.querySelectorAll(`[data-open="${this.id}"]`).forEach((t) => this.listen(t, 'click', (e) => { e.preventDefault(); this.open(t); }));
    this.listen(this, 'keydown', (e) => { if (e.key === 'Escape') this.close(); });
    this.wireControls();
  }
  wireControls() {
    this.panel = this.querySelector('[data-panel]') || this;
    this.querySelectorAll('[data-close]').forEach((c) => { c.onclick = () => this.close(); });
  }
  open(opener) {
    this.setAttribute('data-open-state', 'true');
    this.setAttribute('aria-hidden', 'false');
    lockScroll();
    this._release = trapFocus(this.panel, opener || this._lastOpener);
    bus.emit('overlay:open', { id: this.id });
  }
  close() {
    if (this.getAttribute('data-open-state') !== 'true') return;
    this.setAttribute('data-open-state', 'false');
    this.setAttribute('aria-hidden', 'true');
    unlockScroll();
    if (this._release) { this._release(); this._release = null; }
    bus.emit('overlay:close', { id: this.id });
  }
}

/* ---- Mega-nav: hover-intent + click + keyboard (ADR 0011 floor) ---- */
class MegaNav extends BaseElement {
  init() {
    this.triggers = Array.from(this.querySelectorAll('[data-mega-trigger]'));
    this.hasHover = window.matchMedia('(hover: hover) and (min-width: 990px)');
    this._closeTimer = null;
    this.triggers.forEach((btn) => {
      const panel = this.querySelector(`#${btn.getAttribute('aria-controls')}`);
      this.listen(btn, 'click', () => this.toggle(btn, panel));
      const item = btn.closest('.has-mega');
      if (item) {
        this.listen(item, 'mouseenter', () => { if (this.hasHover.matches) { this.cancel(); this.open(btn, panel); } });
        this.listen(item, 'mouseleave', () => { if (this.hasHover.matches) this.schedule(btn, panel); });
      }
    });
    this.listen(this, 'keydown', (e) => { if (e.key === 'Escape') this.closeAll(true); });
    this.listen(document, 'click', (e) => { if (!this.contains(e.target)) this.closeAll(false); });
    this.listen(this, 'focusout', (e) => { if (!this.contains(e.relatedTarget)) this.closeAll(false); });
  }
  cancel() { if (this._closeTimer) { clearTimeout(this._closeTimer); this._closeTimer = null; } }
  schedule(btn, panel) { this.cancel(); this._closeTimer = setTimeout(() => this.close(btn, panel), 220); }
  toggle(btn, panel) { btn.getAttribute('aria-expanded') === 'true' ? this.close(btn, panel) : this.open(btn, panel); }
  open(btn, panel) { this.cancel(); this.closeAll(false); btn.setAttribute('aria-expanded', 'true'); if (panel) panel.hidden = false; }
  close(btn, panel) { this.cancel(); btn.setAttribute('aria-expanded', 'false'); if (panel) panel.hidden = true; }
  closeAll(refocus) {
    this.triggers.forEach((btn) => {
      const wasOpen = btn.getAttribute('aria-expanded') === 'true';
      this.close(btn, this.querySelector(`#${btn.getAttribute('aria-controls')}`));
      if (wasOpen && refocus) btn.focus();
    });
  }
}

/* ---- Countdown timer (announcement, product urgency). Merchant sets a real
   end time — no fake/rolling urgency. Stops at zero. ---- */
class CountdownTimer extends BaseElement {
  init() {
    const raw = (this.dataset.deadline || '').trim();
    if (!raw) return;
    this.end = new Date(raw.replace(' ', 'T')).getTime();
    if (isNaN(this.end)) return;
    this.parts = {
      d: this.querySelector('[data-cd-part="d"]'), h: this.querySelector('[data-cd-part="h"]'),
      m: this.querySelector('[data-cd-part="m"]'), s: this.querySelector('[data-cd-part="s"]'),
    };
    this.tick();
    this._timer = setInterval(() => this.tick(), 1000);
    this.onCleanup(() => clearInterval(this._timer));
  }
  tick() {
    let left = Math.max(0, this.end - Date.now());
    if (left <= 0) {
      clearInterval(this._timer);
      if (this.dataset.expired) this.textContent = this.dataset.expired;
      return;
    }
    const d = Math.floor(left / 864e5); left -= d * 864e5;
    const h = Math.floor(left / 36e5); left -= h * 36e5;
    const m = Math.floor(left / 6e4); left -= m * 6e4;
    const s = Math.floor(left / 1e3);
    const pad = (n) => String(n).padStart(2, '0');
    if (this.parts.d) this.parts.d.textContent = pad(d);
    if (this.parts.h) this.parts.h.textContent = pad(h);
    if (this.parts.m) this.parts.m.textContent = pad(m);
    if (this.parts.s) this.parts.s.textContent = pad(s);
  }
}

/* ---- Tab group (tabbed collection, PDP info tabs). ARIA tablist with roving
   selection; keyboard arrows + Home/End. Panels hidden via [hidden]. ---- */
class TabGroup extends BaseElement {
  init() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
    this.tabs.forEach((tab, i) => {
      tab.tabIndex = tab.getAttribute('aria-selected') === 'true' ? 0 : -1;
      this.listen(tab, 'click', () => this.select(i));
      this.listen(tab, 'keydown', (e) => this.onKey(e, i));
    });
  }
  select(i) {
    this.tabs.forEach((tab, j) => {
      const on = i === j;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
      if (this.panels[j]) this.panels[j].hidden = !on;
    });
    this.tabs[i].focus();
  }
  onKey(e, i) {
    const last = this.tabs.length - 1;
    let next = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = i === last ? 0 : i + 1;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = i === 0 ? last : i - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next !== null) { e.preventDefault(); this.select(next); }
  }
}

/* ---- Hover video: a product card's clip, played on pointer-over ----
   The <video> ships with preload="none", so a grid of 24 cards costs nothing
   until someone actually hovers one — and the CSS only reveals it under
   `(hover: hover)`, so a phone never downloads any of it.

   Muted is mandatory, not stylistic: every browser refuses a script-initiated
   play() with sound, and refuses it by rejecting a promise nobody sees, so the
   card would simply never animate. The catch is there because a browser may
   refuse anyway (Low Power Mode, data saver) and an unhandled rejection in the
   console helps nobody — the poster frame stays, which is the old behaviour.

   Leaving rewinds rather than pausing: a half-played clip left on screen reads
   as a stuck image the next time the card scrolls past. */
class HoverVideo extends BaseElement {
  init() {
    this.video = this.querySelector('video');
    if (!this.video) return;
    this.video.muted = true;
    const card = this.closest('.dbc') || this;
    this.listen(card, 'pointerenter', () => this.arm());
    this.listen(card, 'pointerleave', () => this.stop());
    // Keyboard users reach the card through its link, never a pointer.
    this.listen(card, 'focusin', () => this.arm());
    this.listen(card, 'focusout', () => this.stop());
    this.onCleanup(() => clearTimeout(this.timer));
  }
  /* A short dwell before the first byte is requested. Dragging the pointer
     across a 12-card grid would otherwise start twelve downloads of a file
     nobody waited to watch; Liquid exposes only the 1080p rendition, so each
     one is expensive. */
  arm() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.start(), 120);
  }
  start() {
    if (!this.video || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.setAttribute('data-playing', 'true');
    const started = this.video.play();
    if (started && started.catch) started.catch(() => this.removeAttribute('data-playing'));
  }
  stop() {
    clearTimeout(this.timer);
    if (!this.video) return;
    this.removeAttribute('data-playing');
    try { this.video.pause(); this.video.currentTime = 0; } catch (_) { /* not seekable yet */ }
  }
  teardown() { this.stop(); }
}

define('announcement-bar', AnnouncementBar);
define('hover-video', HoverVideo);
define('quantity-input', QuantityInput);
define('overlay-drawer', OverlayDrawer);
define('mega-nav', MegaNav);
define('countdown-timer', CountdownTimer);
define('tab-group', TabGroup);
reviveOnSectionLoad('announcement-bar');
reviveOnSectionLoad('mega-nav');
reviveOnSectionLoad('countdown-timer');
